import { cur } from '@/lib/currency';
import { SectionBar, pageLink, amountCols } from '@/components/accounting/SectionBar';

const fmt = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const Row = ({ label, value, strong, muted, testid }) => (
  <div className={`flex justify-between gap-3 py-1 text-sm ${strong ? 'font-semibold border-t border-border mt-1 pt-2' : ''} ${muted ? 'text-muted-foreground text-xs' : ''}`}>
    <span>{label}</span><span className="tabular-nums" data-testid={testid}>{fmt(value)}</span>
  </div>
);
const sub = (per) => `${per?.start} to ${per?.end}${per?.branchId ? ' · selected branch' : ' · all permitted branches'}`;
const ITEM_PAGE = { sales_not_posted: '/sales', expenses_not_posted: '/expenses' };

// GL net profit vs operational (daily records) — each line explains part of the difference; per-account table drills into the GL.
export function PnlReconciliation({ rec, per, onDrill }) {
  if (!rec) return null;
  const items = rec.items.filter((i) => Math.abs(i.effect_on_profit) >= 0.01);
  const byAcc = rec.cost_by_account || [];
  const spec = () => ({ title: 'P&L · Daily records vs posted GL', subtitle: sub(per), filename: `pnl_reconciliation_${per?.start}_${per?.end}`, cols: amountCols,
    rows: [{ item: 'Operational net profit (daily records)', value: rec.operational_net }, ...items.map((i) => ({ item: i.label, value: i.effect_on_profit })),
      { item: 'Accounting net profit (posted GL)', value: rec.gl_net }, { item: 'Difference', value: rec.difference },
      ...byAcc.map((a) => ({ item: `By account · ${a.code || '—'} ${a.account} (daily ${a.operational.toFixed(2)} / GL ${a.gl.toFixed(2)})`, value: a.difference }))] });
  return (
    <div className="rounded-xl border border-border p-4 space-y-1" data-testid="pnl-reconciliation">
      <div className="text-sm font-bold flex items-center justify-between gap-2 flex-wrap">Reconciliation: accounting (GL) vs operational records <SectionBar testId="pnl-sec-recon" spec={spec} /></div>
      <p className="text-[11px] text-muted-foreground mb-2">{rec.note}</p>
      <Row label="Operational net profit (daily records)" value={rec.operational_net} testid="rec-operational-net" />
      {items.map((i) => (
        <div key={i.key} className="flex items-center justify-between gap-2 text-xs text-muted-foreground py-0.5">
          <span>{`${i.effect_on_profit >= 0 ? '+' : '−'} ${i.label}${i.count ? ` (${i.count})` : ''}`}
            {ITEM_PAGE[i.key] && per && <a href={pageLink(ITEM_PAGE[i.key], per)} className="ml-2 text-primary underline" data-testid={`rec-${i.key}-open`}>open records</a>}</span>
          <span className="tabular-nums" data-testid={`rec-${i.key}`}>{fmt(Math.abs(i.effect_on_profit))}</span>
        </div>
      ))}
      <Row strong label="Accounting net profit (posted GL)" value={rec.gl_net} testid="rec-gl-net" />
      {byAcc.length > 0 && (
        <details className="pt-2" data-testid="rec-by-account">
          <summary className="cursor-pointer text-xs font-semibold text-primary">Where the cost difference sits (by account) · click an account for its GL entries</summary>
          <table className="w-full text-[11px] mt-1">
            <thead><tr className="text-muted-foreground"><th className="text-left">Account</th><th className="text-right">Daily records</th><th className="text-right">Posted GL</th><th className="text-right">GL − daily</th></tr></thead>
            <tbody>{byAcc.map((a) => (
              <tr key={`${a.code}-${a.account}`} className="border-t border-border">
                <td>{a.code && onDrill ? <button type="button" className="text-primary underline" onClick={() => onDrill(a.code, a.account)} data-testid={`rec-acc-${a.code}`}>{a.code} {a.account}</button> : <span>{a.account} <span className="text-muted-foreground">(mapped account, nothing posted)</span></span>}</td>
                <td className="text-right tabular-nums">{fmt(a.operational)}</td><td className="text-right tabular-nums">{fmt(a.gl)}</td><td className="text-right tabular-nums font-semibold">{fmt(a.difference)}</td>
              </tr>))}</tbody>
          </table>
        </details>
      )}
    </div>
  );
}

const Block = ({ title, children, testid }) => (
  <div className="rounded-lg border border-border p-3" data-testid={testid}>
    <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">{title}</div>
    {children}
  </div>
);

// Cash, bank and supplier-credit movements for the selection (below Net Profit; never changes profit).
export function PnlCashCreditSection({ sup, per }) {
  if (!sup) return null;
  const { cash_bank: cb, supplier_credit: sc } = sup;
  const v = cb?.verified || {};
  const spec = () => ({ title: 'Cash, bank and supplier credit (not profit)', subtitle: sub(per), filename: `pnl_cash_bank_supplier_${per?.start}_${per?.end}`, cols: amountCols,
    rows: [...['cash', 'bank'].flatMap((k) => ['opening', 'received', 'paid', 'transfers_net', 'closing'].map((f) => ({ item: `${k} (posted GL) · ${f.replace('_', ' ')}`, value: cb?.[k]?.[f] }))),
      ...['opening_outstanding', 'paid_immediately', 'new_credit', 'paid_current_credit', 'paid_previous_credit', 'advance_or_unmatched', 'closing_outstanding'].map((f) => ({ item: `supplier · ${f.replace(/_/g, ' ')}`, value: sc?.[f] }))] });
  return (
    <div className="space-y-3" data-testid="pnl-cash-credit">
      <div className="text-sm font-bold flex items-center justify-between gap-2 flex-wrap">Cash, bank and supplier credit (does not change profit) <SectionBar testId="pnl-sec-cash-supplier" detailsTo="/suppliers" spec={spec} /></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {['cash', 'bank'].map((k) => (
          <Block key={k} title={k === 'cash' ? 'Cash (posted GL)' : 'Bank (posted GL)'} testid={`pnl-${k}-block`}>
            <Row label="Opening" value={cb[k].opening} muted />
            <Row label="Received" value={cb[k].received} />
            <Row label="Paid" value={cb[k].paid} />
            {Math.abs(cb[k].transfers_net) >= 0.01 && <Row label="Internal transfers (net)" value={cb[k].transfers_net} muted />}
            <Row label="Closing — calculated, UNVERIFIED" value={cb[k].closing} strong testid={`pnl-${k}-closing`} />
          </Block>
        ))}
        <Block title="Verified vs unverified balances" testid="pnl-verified-block">
          <VerifiedBalances v={v} />
        </Block>
      </div>
      <Block title="Supplier purchases and credit" testid="pnl-supplier-block">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <div>
            <Row label="Purchases paid immediately" value={sc.paid_immediately} testid="sc-paid-immediately" />
            <Row label="New credit purchases" value={sc.new_credit} testid="sc-new-credit" />
            <Row label="Paid against this period's credit" value={sc.paid_current_credit} testid="sc-paid-current" />
            <Row label="Remaining credit from this period" value={sc.remaining_current_credit} strong testid="sc-remaining-current" />
          </div>
          <div>
            <Row label="Supplier credit at period start" value={sc.opening_outstanding} muted />
            <Row label="Paid against previous periods' credit (not an expense)" value={sc.paid_previous_credit} testid="sc-paid-previous" />
            {Math.abs(sc.advance_or_unmatched) >= 0.01 && <Row label="Advances / payments without a matching bill" value={sc.advance_or_unmatched} muted />}
            <Row label="Total supplier outstanding at period end" value={sc.closing_outstanding} strong testid="sc-closing-outstanding" />
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">{sc.basis}</p>
      </Block>
    </div>
  );
}

const STATUS = { VERIFIED: 'VERIFIED', STATEMENT_NOT_RECONCILED: 'UNVERIFIED · statement not reconciled', STALE: 'UNVERIFIED · statement out of date', NO_STATEMENT: 'UNVERIFIED · no statement' };

// Verified money only (reconciled statement / cashier count). Everything else is UNVERIFIED and never added to the verified total.
export function VerifiedBalances({ v }) {
  if (!v) return null;
  const cashOk = (v.cash_counts || []).filter((c) => c.verified);
  const any = v.bank_total_verified != null || v.cash_total_verified != null;
  return (
    <div className="space-y-0.5" data-testid="verified-balances">
      <div className="flex justify-between text-xs font-semibold py-0.5"><span>Verified cash + bank</span>
        <span data-testid="verified-total">{any ? fmt((v.bank_total_verified ?? 0) + (v.cash_total_verified ?? 0)) : 'None verified'}</span></div>
      {(v.bank_accounts || []).map((a) => (
        <div key={a.account_id} className="flex justify-between gap-2 text-[11px] py-0.5" title={a.reason || ''} data-testid={`vb-acct-${a.account_id}`}>
          <span className="truncate">{a.name}</span>
          <span className={a.verified ? 'font-semibold text-emerald-700' : 'text-amber-700'}>
            {a.verified ? fmt(a.balance) : `${STATUS[a.status] || 'UNVERIFIED'}${a.statement_balance != null ? ` · stmt ${fmt(a.statement_balance)}` : ''}`}
          </span>
        </div>
      ))}
      {cashOk.map((c) => (
        <div key={c.branch_id} className="flex justify-between text-[11px] py-0.5"><span>Cash count {c.as_of}</span><span className="font-semibold text-emerald-700">{fmt(c.balance)}</span></div>
      ))}
      <p className="text-[10px] text-muted-foreground mt-1">{v.note}</p>
    </div>
  );
}

// GL completeness, internal inter-branch margin and credit-balance warnings (read-only explanations).
export function PnlCompleteness({ data }) {
  const c = data?.completeness, ib = data?.inter_branch_margin, neg = data?.negative_accounts || [];
  if (!c) return null;
  return (
    <div className="space-y-2" data-testid="pnl-completeness">
      {!c.gl_complete && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs space-y-1" data-testid="pnl-gl-incomplete">
          <div className="font-semibold text-amber-800">General Ledger incomplete for this selection</div>
          {c.unposted_sales_count > 0 && <div>{c.unposted_sales_count} sale(s) not posted · net {fmt(c.unposted_sales_net)}</div>}
          {c.unposted_costs_count > 0 && <div>{c.unposted_costs_count} expense / salary record(s) not posted · {fmt(c.unposted_costs)}</div>}
          <div className="font-medium" data-testid="pnl-estimate">ESTIMATE only — not the official accounting result: net profit if the missing records were posted {fmt(c.net_including_unposted)}{c.margin_including_unposted != null ? ` · margin ${c.margin_including_unposted}%` : ''}</div>
          <div className="text-[10px] text-muted-foreground">The official result is the posted General Ledger figure above. Nothing is posted automatically.</div>
          {c.margin_note && <div className="text-amber-800">{c.margin_note}</div>}
        </div>
      )}
      {ib && Math.abs(ib.amount) >= 0.01 && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 text-xs" data-testid="pnl-inter-branch-margin">
          <div className="flex justify-between font-semibold"><span>Inter-branch invoice margin {ib.included_in_profit ? '(included)' : '(eliminated — not company profit)'}</span><span>{fmt(ib.amount)}</span></div>
          <div className="text-muted-foreground">{ib.note}{ib.posted_to?.length ? ` Posted to: ${ib.posted_to.join(', ')}.` : ''}</div>
        </div>
      )}
      {neg.length > 0 && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs" data-testid="pnl-negative-accounts">
          <div className="font-semibold text-rose-800">Accounts with a credit (negative) balance — review</div>
          {neg.map((a) => <div key={a.code} className="flex justify-between"><span>{a.code} {a.name}</span><span>{fmt(a.amount)}</span></div>)}
        </div>
      )}
    </div>
  );
}
